/**
 * ASCII renderer.
 *
 * ## Why this exists
 *
 * A screenshot answers "does it look right". It cannot answer "what is at tile
 * 67, row 24", and it cannot be read in a terminal, diffed, grepped, or pasted
 * into a bug report. Every time a level question comes up — why is Tavi
 * falling here, what is that object at (155,11), is that gap really four tiles
 * — the answer has historically been a throwaway script that dumps a terrain
 * grid and a list of coordinates. Those scripts get written again and again,
 * they are never tested, and they each invent their own glyphs.
 *
 * This is that tool, done once. It renders any rectangular region of a level —
 * the live viewport by default — as text, with a legend, so the answer to a
 * level question is a string you can read, diff and paste.
 *
 * ## Design
 *
 * It is a pure function from state to string. No canvas, no DOM, no globals,
 * which is what makes it testable and usable from a node script as well as from
 * `__debug.ascii()` in the browser.
 *
 * Precedence matters and is fixed, because a glyph that silently changes
 * meaning between two renders is worse than no glyph. Draw order, back to
 * front: terrain, then slopes, then placed objects, then entities, then the
 * player. So a rivet standing on a pipe is a rivet, and the player standing
 * on the ground is a player, and anything you cannot see is because something
 * opaque is genuinely in front of it.
 */

import { TILE } from '../engine/constants'
import { Slope } from '../engine/collision'
import { Terrain } from '../game/types'

/** The minimum a region needs to be renderable. */
export interface AsciiSource {
  width: number
  height: number
  /** Terrain id at a tile; out of bounds reads as `Terrain.EMPTY`. */
  terrainAt(tx: number, ty: number): number
  isSolid(tx: number, ty: number): boolean
  isOneWay(tx: number, ty: number): boolean
  slopeAt(tx: number, ty: number): Slope
  /** Live objects, for the object layer. */
  objects?: {
    tx: number
    ty: number
    alive: boolean
    def: { id: string; w: number; h: number }
  }[]
  /** Live entities, for the entity layer. */
  entities?: { x: number; y: number; alive: boolean; kind: string }[]
  /** The player, drawn last so they are never hidden. */
  player?: {
    x: number
    y: number
    w: number
    h: number
    alive: boolean
    shieldTime?: number
  }
}

export interface AsciiOptions {
  /** Tile column to start at. Defaults to the left edge of the region asked for. */
  tx?: number
  ty?: number
  /** Size in tiles. Defaults to the whole level. */
  cols?: number
  rows?: number
  /** Draw a legend of every glyph in use. On by default. */
  legend?: boolean
  /** Draw tile coordinate rulers down the side and along the top. */
  rulers?: boolean
  /** Mark lethal tiles and objects with `!` in the margin. Off by default. */
  markLethal?: boolean
  /** Override the object glyph for a specific object id. */
  glyphs?: Record<string, string>
}

/**
 * Terrain glyphs.
 *
 * Uppercase is solid, lowercase is one-way, so solidity is readable at a glance
 * without a legend lookup. Slopes override these because a slope is solid *and*
 * directional, and the direction is the whole point of it.
 */
const TERRAIN_GLYPH: Record<number, string> = {
  [Terrain.GROUND]: '#',
  [Terrain.HARD]: 'H',
  [Terrain.BRICK]: 'B',
  [Terrain.SLOPE_L]: '/',
  [Terrain.SLOPE_R]: '\\',
  [Terrain.PLATFORM]: '=',
}

/**
 * Object glyphs, by registry id.
 *
 * Only the ones worth telling apart get an entry; everything else falls back to
 * the first letter of its id, uppercased. A fallback is deliberate: 46 ids is
 * more than there are legible ASCII characters, and an unlisted object should
 * still be *visible* rather than silently blank.
 */
const OBJECT_GLYPH: Record<string, string> = {
  'crate-expansion-cell': '?',
  'crate-ember-cell': 'F',
  'crate-prism': '*',
  'crate-coin': 'o',
  'crate-spare-battery': '1',
  brick: 'B',
  'hard-block': 'H',
  'invisible-block': 'I',
  'note-block': 'n',
  spring: '^',
  pipe: 'P',
  spike: '^',
  lava: '~',
  coin: 'o',
  'moving-platform': 'M',
  lift: 'L',
  vine: 'Y',
  switch: 'S',
  checkpoint: 'C',
  goal: 'G',
  'donut-block': 'D',
  'boost-block': 'B',
  'falling-platform': 'F',
}

/** Entity glyphs. Uppercase for anything that can hurt you. */
const ENTITY_GLYPH: Record<string, string> = {
  rivet: 'g',
  scout: 'k',
  wanderer: 'r',
  shardling: 's',
  wisp: 'b',
  'expansion-cell': 'm',
  'ember-cell': 'f',
  prism: '*',
  'spare-battery': '1',
  coin: 'o',
  'coin-pop': 'o',
  fireball: 'x',
  debris: 'd',
  'rivet-squash': 'd',
  poof: '.',
}

/** What each glyph means, for the legend. */
const LEGEND: Record<string, string> = {
  '@': 'the player',
  '*': 'prism (or a prism block)',
  '!': 'lethal: touching this without a prism kills',
  '/': 'slope, high on the right',
  '\\': 'slope, high on the left',
  '#': 'solid ground',
  H: 'solid hard block',
  B: 'brick',
  '=': 'one-way platform (land on it from above only)',
  '^': 'spike / spring',
  '~': 'lava',
  P: 'pipe',
  M: 'moving platform',
  L: 'lift',
  Y: 'vine',
  S: 'switch',
  C: 'checkpoint',
  G: 'goal',
  o: 'coin',
  '?': 'item block',
  F: 'fire block / falling platform',
  m: 'expansion cell',
  f: 'ember cell',
  '1': 'spare battery',
  x: 'fireball',
  g: 'rivet',
  k: 'scout',
  r: 'wandering scout',
  s: 'shardling',
  b: 'wisp',
  d: 'debris',
  I: 'hidden cache',
  n: 'note block',
  D: 'donut block',
}

/** Anything not in the legend. Kept short so the legend stays readable. */
const UNKNOWN_LEGEND = 'object'

/**
 * Render a region of a level as text.
 *
 * Returns a multi-line string with no trailing newline. Never throws on
 * out-of-bounds coordinates: a region may legitimately hang off the edge of the
 * course, and a diagnostic that crashes while describing a bug is worse than
 * useless.
 */
export function renderAscii(
  source: AsciiSource,
  opts: AsciiOptions = {},
): string {
  const cols = Math.max(1, Math.min(opts.cols ?? source.width, source.width))
  const rows = Math.max(1, Math.min(opts.rows ?? source.height, source.height))
  const tx = clampInt(opts.tx ?? 0, 0, Math.max(0, source.width - 1))
  const ty = clampInt(opts.ty ?? 0, 0, Math.max(0, source.height - 1))

  const grid: string[][] = []
  const legendUsed = new Set<string>()

  for (let y = 0; y < rows; y++) {
    const line: string[] = []
    for (let x = 0; x < cols; x++) {
      const ch = glyphAt(source, tx + x, ty + y, opts.glyphs)
      line.push(ch)
      if (ch !== ' ') legendUsed.add(ch)
    }
    grid.push(line)
  }

  const ruler = opts.rulers === true
  const gutter = ruler ? String(ty + rows - 1).length + 1 : 0
  const out: string[] = []

  if (ruler) {
    // Column ruler, labelled every 10 tiles so a position can be cited rather
    // than counted.
    const head = ' '.repeat(gutter)
    let marks = ''
    for (let x = 0; x < cols; x++) {
      const abs = tx + x
      marks += abs % 10 === 0 ? String(Math.floor(abs / 10) % 10) : ' '
    }
    out.push(head + marks)
  }

  for (let y = 0; y < rows; y++) {
    const label = ruler ? String(ty + y).padStart(gutter - 1) + ' ' : ''
    out.push(label + (grid[y]?.join('') ?? ''))
  }

  if (opts.markLethal === true) {
    out.push('')
    out.push(markLethalRows(source, tx, ty, cols, rows))
  }

  if (opts.legend !== false) {
    const entries: string[] = []
    for (const ch of legendUsed) {
      entries.push(`  ${ch}  ${LEGEND[ch] ?? UNKNOWN_LEGEND}`)
    }
    entries.sort()
    if (entries.length > 0) out.push('', ...entries)
  }

  return out.join('\n')
}

/** The glyph for one tile, applying the fixed draw order. */
function glyphAt(
  source: AsciiSource,
  tx: number,
  ty: number,
  overrides?: Record<string, string>,
): string {
  // Player last: they are the one thing that must never be hidden.
  const p = source.player
  if (p !== undefined && p.alive && overlapsTile(p.x, p.y, p.w, p.h, tx, ty)) {
    return (p.shieldTime ?? 0) > 0 ? '*' : '@'
  }

  for (const e of source.entities ?? []) {
    if (!e.alive) continue
    if (overlapsTile(e.x, e.y, 16, 16, tx, ty)) {
      return ENTITY_GLYPH[e.kind] ?? e.kind.charAt(0).toUpperCase()
    }
  }

  for (const o of source.objects ?? []) {
    if (!o.alive) continue
    if (tx < o.tx || tx >= o.tx + o.def.w) continue
    if (ty < o.ty || ty >= o.ty + o.def.h) continue
    const id = o.def.id
    if (overrides !== undefined && overrides[id] !== undefined)
      return overrides[id]
    return OBJECT_GLYPH[id] ?? id.charAt(0).toUpperCase()
  }

  const slope = safeSlope(source, tx, ty)
  if (slope === Slope.LEFT) return '/'
  if (slope === Slope.RIGHT) return '\\'

  if (safeSolid(source, tx, ty))
    return TERRAIN_GLYPH[source.terrainAt(tx, ty)] ?? '#'
  if (safeOneWay(source, tx, ty)) return '='
  return ' '
}

/** Does a world-pixel box cover any part of this tile? */
function overlapsTile(
  x: number,
  y: number,
  w: number,
  h: number,
  tx: number,
  ty: number,
): boolean {
  return (
    x < (tx + 1) * TILE &&
    x + w > tx * TILE &&
    y < (ty + 1) * TILE &&
    y + h > ty * TILE
  )
}

/**
 * Lethal tiles in each row, as a right-hand margin.
 *
 * A separate margin rather than a glyph, because a lethal tile is usually drawn
 * as perfectly ordinary-looking geometry — lava is a solid row of ground, and a
 * spike strip looks like a strip. Overwriting the glyph would destroy the very
 * information you need in order to judge the hazard.
 */
function markLethalRows(
  source: AsciiSource,
  tx: number,
  ty: number,
  cols: number,
  rows: number,
): string {
  const lethal = new Set(['spike', 'lava'])
  const lines: string[] = []
  for (let y = 0; y < rows; y++) {
    let mark = ''
    for (let x = 0; x < cols; x++) {
      const hit = (source.objects ?? []).some(
        (o) =>
          o.alive &&
          lethal.has(o.def.id) &&
          tx + x >= o.tx &&
          tx + x < o.tx + o.def.w &&
          ty + y >= o.ty &&
          ty + y < o.ty + o.def.h,
      )
      mark += hit ? '!' : ' '
    }
    if (mark.trim() !== '') lines.push(`  row ${ty + y}: |${mark}|`)
  }
  return lines.length > 0
    ? lines.join('\n')
    : '  no lethal objects in this region'
}

// Every accessor is guarded: `renderAscii` is a diagnostic, and it must survive
// being pointed at a half-built or off-the-edge level rather than throwing while
// trying to explain what is wrong.
function safeSolid(source: AsciiSource, tx: number, ty: number): boolean {
  try {
    return source.isSolid(tx, ty)
  } catch {
    return false
  }
}

function safeOneWay(source: AsciiSource, tx: number, ty: number): boolean {
  try {
    return source.isOneWay(tx, ty)
  } catch {
    return false
  }
}

function safeSlope(source: AsciiSource, tx: number, ty: number): Slope {
  try {
    return source.slopeAt(tx, ty)
  } catch {
    return Slope.NONE
  }
}

function clampInt(v: number, lo: number, hi: number): number {
  if (!Number.isFinite(v)) return lo
  return Math.max(lo, Math.min(hi, Math.round(v)))
}
